import { LinkedResourceType } from 'gen/api';
import { createContext, useContext } from 'react';
import { DocumentType, IHighlightInfo, IOfficeActionTab, TFileType } from '../../types';

// Navigation panel states
export enum NavigationPanelState {
  LOADING = 'LOADING',
  APPLICATION_INPUT = 'APPLICATION_INPUT',
  FILE_MANAGEMENT = 'FILE_MANAGEMENT',
  DOCUMENT_NAVIGATION = 'DOCUMENT_NAVIGATION',
}

interface IOfficeActionActions {
  // Dynamic panel actions
  createPanel: (name?: string) => string;
  openTabInPanel: (panelId: string, tab: IOfficeActionTab) => void;
  moveTabToPanel: (sourcePanelId: string, targetPanelId: string, tab: IOfficeActionTab) => void;

  // Resources
  addCurrentResource: (resource: { id: string; title: string; type: LinkedResourceType }) => void;
  removeCurrentResource: (resourceId: string) => void;
  clearCurrentResources: () => void;
  setCurrentResources: (resources: { id: string; title: string; type: LinkedResourceType }[]) => void;

  // Reset actions
  resetOfficeAction: () => void;

  // Panel control actions
  showPanel?: (panelId: string) => void;

  // Actions for dynamic panels
  openFileInNewPanel: (fileId: string, fileName: string, highlight?: IHighlightInfo, fileType?: TFileType) => void;
  openFileInPanel: (
    panelId: string,
    fileId: string,
    fileName: string,
    highlight?: IHighlightInfo,
    fileType?: TFileType,
  ) => void;
  openObjectionInNewPanel: (objectionId: string, objectionName?: string) => void;

  // Open in first panel or create one if none exist
  openTabInFirstPanel: (tab: IOfficeActionTab) => void;

  // Editor helper functions
  isEditorOpen: (editorType: DocumentType) => boolean;
}

export type OfficeActionContextType = IOfficeActionActions;

export const OfficeActionContext = createContext<OfficeActionContextType | null>(null);

export const useOfficeAction = () => {
  const context = useContext(OfficeActionContext);
  if (context === null) {
    throw new Error('useOfficeAction must be used within an OfficeActionProvider');
  }
  return context;
};
